// src/utils/launchRollup.js
//
// NOTE (WP6, docs/mcp-signoff-spec.md §6/§9): the AUTHORITATIVE sign-off
// verdict now lives on the proxy (proxy-server/launch/rollup.js — a verbatim
// CommonJS port of this file, plus buildRollup.js on top of it), served by
// GET /proxy/launches/<id>/rollup. LaunchPage.jsx fetches that endpoint and no
// longer calls criterionStatus()/launchRollup() from here to decide what a
// launch's verdict IS. This copy survives ONLY for:
//   - CapturePanel.jsx's live in-flight coverage tracking (utils/launchCoverage.js,
//     which imports launchRollup() for its autoDraft rollup) — a tight loop over
//     results that are not yet recorded/attached on the proxy, which the rollup
//     endpoint (reads recorded runs) cannot serve;
//   - utils/buildLaunchReport.js's HTML/print sign-off report, which stays
//     frontend-owned (DOM/print styling the proxy has no use for).
// Any RULE change here (the criterion-status logic, the rollup counts) must be
// mirrored in proxy-server/launch/rollup.js or the two copies silently drift —
// see docs/mcp-signoff-spec.md §4's shared-fixture mitigation.
//
// Pure criterion-status rollup for the Launch workspace (WP4,
// docs/launch-signoff-spec.md §7). Given one criterion (from a launch
// record's `criteria[]`, WP2 §5 shape) and a lookup of the LATEST check
// results keyed by checkId (built from a /proxy/checks/run report's
// `results[]`), decides the criterion's overall status for display and for
// the eventual sign-off rollup (WP5 reuses/mirrors this).
//
// Rules (spec §7, as implemented):
//   - mapping === 'manual'                        -> 'manual-review' (always,
//     regardless of any checks — a manual criterion is never provably "met"
//     from traffic; it's acknowledged in the sign-off flow instead, WP5)
//   - mapping === 'unmapped', or no checkIds at all -> 'not-run'
//   - checkIds present but none have a result yet   -> 'not-run' (never run,
//     or the run didn't cover this checkId)
//   - any mapped check's result.status === 'fail'    -> 'not-met'
//   - else any mapped check's result.status === 'warn' -> 'needs-review'
//   - else every mapped check's result.status === 'pass' -> 'met'
//   - else (a mix of pass/n/a/info with no fail/warn — e.g. a check that
//     came back 'n/a' because nothing in scope matched its target) ->
//     'needs-review'. The spec doesn't enumerate this case explicitly; this
//     is a deliberately conservative choice — a criterion is never reported
//     "Met" unless every one of its mapped checks actually passed.
//   - EXCEPTION to the line above: an 'n/a' from a check the compiler ATTACHED
//     (criterion.baselineIds — the whole-family conformance backstop) is
//     ignored. Those checks widen coverage beyond what the criterion named, so
//     one that didn't apply to this session is silence rather than a review
//     item; a fail/warn from them counts in full.
//
// Never throws: a malformed criterion/lookup degrades to 'not-run'.

import { UI_WORDING } from './launchWording';

export const CRITERION_STATUS = {
  MET: 'met',
  NOT_MET: 'not-met',
  NEEDS_REVIEW: 'needs-review',
  MANUAL_REVIEW: 'manual-review',
  NOT_RUN: 'not-run',
};

export const CRITERION_STATUS_LABEL = {
  [CRITERION_STATUS.MET]: 'Met',
  [CRITERION_STATUS.NOT_MET]: 'Not met',
  [CRITERION_STATUS.NEEDS_REVIEW]: 'Needs review',
  [CRITERION_STATUS.MANUAL_REVIEW]: 'Manual review',
  [CRITERION_STATUS.NOT_RUN]: 'Not run',
};

/**
 * resultsByCheckId: plain object OR Map, checkId -> latest result
 * ({ checkId, status, matched, found, failures, rows, ... } — the
 * evaluateCheck() shape echoed by /proxy/checks/run's `results[]`).
 */
function lookupResult(resultsByCheckId, checkId) {
  if (!resultsByCheckId || !checkId) return null;
  try {
    if (typeof resultsByCheckId.get === 'function') return resultsByCheckId.get(checkId) || null;
    return resultsByCheckId[checkId] || null;
  } catch {
    return null;
  }
}

/**
 * criterionStatus(criterion, resultsByCheckId) -> one of CRITERION_STATUS.
 * Never throws.
 */
export function criterionStatus(criterion, resultsByCheckId, mode) {
  try {
    if (!criterion || typeof criterion !== 'object') return CRITERION_STATUS.NOT_RUN;
    if (criterion.mapping === 'manual') return CRITERION_STATUS.MANUAL_REVIEW;

    const checkIds = Array.isArray(criterion.checkIds) ? criterion.checkIds.filter(Boolean) : [];
    if (criterion.mapping === 'unmapped' || !checkIds.length) return CRITERION_STATUS.NOT_RUN;

    // Baseline checks the compiler ATTACHED (criterion.baselineIds — the
    // whole-family conformance backstop in compileCriteria.js) that came back
    // 'n/a' are dropped here. The criterion never named them; they were added
    // to widen coverage, and one that didn't apply to this session is silence,
    // not a review item. Without this, attaching a family's baseline would push
    // every broad criterion to "Needs review" on the first inapplicable check.
    // A baseline check that FAILS or WARNS still counts in full — that is the
    // entire reason for attaching it.
    const attached = new Set(Array.isArray(criterion.baselineIds) ? criterion.baselineIds : []);
    const statuses = checkIds
      .map((id) => ({ id, status: lookupResult(resultsByCheckId, id)?.status }))
      .filter((r) => r.status && !(attached.has(r.id) && r.status === 'n/a'))
      .map((r) => r.status);
    if (!statuses.length) return CRITERION_STATUS.NOT_RUN;

    // 'n/a' reads as "not evaluated": every result n/a -> not-run (parity with the proxy copy, C20).
    // Gated by LAUNCH_REDESIGN: only mode === 'on' returns not-run; off/shadow/unknown keep the
    // pre-redesign frontend answer (falls through to needs-review below).
    if (mode === 'on' && statuses.every((x) => x === 'n/a')) return CRITERION_STATUS.NOT_RUN;

    if (statuses.includes('fail')) return CRITERION_STATUS.NOT_MET;
    if (statuses.includes('warn')) return CRITERION_STATUS.NEEDS_REVIEW;
    if (statuses.every((s) => s === 'pass')) return CRITERION_STATUS.MET;
    return CRITERION_STATUS.NEEDS_REVIEW;
  } catch {
    return CRITERION_STATUS.NOT_RUN;
  }
}

/**
 * criterionResults(criterion, resultsByCheckId) -> [{ checkId, result|null }]
 * for each of the criterion's checkIds — the per-check breakdown a detail
 * card renders alongside the rolled-up status. Never throws.
 */
export function criterionResults(criterion, resultsByCheckId) {
  const checkIds = Array.isArray(criterion?.checkIds) ? criterion.checkIds.filter(Boolean) : [];
  return checkIds.map((checkId) => ({ checkId, result: lookupResult(resultsByCheckId, checkId) }));
}

/**
 * Build a { met, notMet, needsReview, manualReview, notRun } rollup across a
 * launch's whole criteria[] — used for the compact summary strip above the
 * per-criterion cards.
 */
export function launchRollup(criteria, resultsByCheckId, mode) {
  const out = { met: 0, notMet: 0, needsReview: 0, manualReview: 0, notRun: 0 };
  for (const c of Array.isArray(criteria) ? criteria : []) {
    const s = criterionStatus(c, resultsByCheckId, mode);
    if (s === CRITERION_STATUS.MET) out.met += 1;
    else if (s === CRITERION_STATUS.NOT_MET) out.notMet += 1;
    else if (s === CRITERION_STATUS.NEEDS_REVIEW) out.needsReview += 1;
    else if (s === CRITERION_STATUS.MANUAL_REVIEW) out.manualReview += 1;
    else out.notRun += 1;
  }
  return out;
}

export default criterionStatus;

// ---------------------------------------------------------------------------
// Unit model (launch-redesign T12; twin of proxy-server/launch/units.js + criterionRollup in
// proxy-server/launch/rollup.js - parity: launchRollupParity.test.js). Keep the two in lockstep.
// ---------------------------------------------------------------------------

export const STATUSES = Object.freeze(['met', 'not_met', 'not_covered', 'needs_review', 'not_run', 'manual', 'waived']);
export const UNIT_REASONS = Object.freeze({ no_branch_applies: 'no_branch_applies' });
export const COVERAGE_GAP_REASONS = Object.freeze([
  'no_rows_matched', 'scope_unreadable', 'unreadable_truncated_url',
  'unreadable_no_request_body', 'unreadable_no_response', 'unreadable_body_stripped',
]);
export const DEFAULT_UNIT = '_default';
export const OBSERVE_UNIT = '_observe';
export const NO_BRANCH_UNIT = '_no_branch';

const LEGACY_TO_STATUS = Object.freeze({
  met: 'met', 'not-met': 'not_met', 'needs-review': 'needs_review', 'manual-review': 'manual', 'not-run': 'not_run',
});
const STATUS_TO_LEGACY = Object.freeze({
  met: 'met', not_met: 'not-met', needs_review: 'needs-review', manual: 'manual-review', not_run: 'not-run',
  not_covered: 'not-run', waived: 'needs-review',
});
export const fromLegacyStatus = (s) => LEGACY_TO_STATUS[s] || 'not_run';
export const toLegacyStatus = (s) => STATUS_TO_LEGACY[s] || 'not-run';

export function effectiveNaReason(r) {
  if (!r || r.status !== 'n/a') return null;
  if (r.naReason) return r.naReason;
  return Number(r.matched) === 0 ? 'no_rows_matched' : 'value_absent';
}

function gradedCounts(entries) {
  let graded = 0;
  let unreadable = 0;
  for (const e of entries) {
    const r = e.result;
    const ev = Array.isArray(r.evidence) ? r.evidence.filter((x) => x && x.unreadable === true).length : 0;
    unreadable += Math.max(ev, Number(r.scopeUnreadable) || 0);
    graded += Array.isArray(r.gradedIds) ? r.gradedIds.length : Math.max(0, (Number(r.matched) || 0) - ev);
  }
  return { graded, unreadable };
}

/** entries: [{ checkId, result|null }]; opts: { attached, waived, reason }. */
export function unitStatus(entries, opts = {}) {
  try {
    if (opts.reason === UNIT_REASONS.no_branch_applies) return { status: 'needs_review', reason: UNIT_REASONS.no_branch_applies };
    const attached = new Set(opts.attached || []);
    const live = (Array.isArray(entries) ? entries : [])
      .filter((e) => e && e.result && e.result.status && !(attached.has(e.checkId) && e.result.status === 'n/a'));
    if (!live.length) return { status: 'not_run' };
    const sts = live.map((e) => e.result.status);
    if (sts.includes('fail')) return { status: 'not_met' };
    const gap = live.some((e) => COVERAGE_GAP_REASONS.includes(effectiveNaReason(e.result)));
    if (gap) return opts.waived ? { status: 'waived' } : { status: 'not_covered' };
    if (sts.every((x) => x === 'n/a')) return { status: 'needs_review' };
    if (sts.every((x) => x === 'pass')) return { status: 'met', ...gradedCounts(live) };
    return { status: 'needs_review' };
  } catch {
    return { status: 'not_run' };
  }
}

/** C13 order. Named unitsCriterionStatus here because criterionStatus above is the legacy surface. */
export function unitsCriterionStatus(units, opts = {}) {
  if (opts.manual) return 'manual';
  const list = Array.isArray(units) ? units : [];
  if (!list.length) return 'not_run';
  const has = (s) => list.some((u) => u.status === s);
  if (has('not_met')) return 'not_met';
  if (has('not_covered')) return 'not_covered';
  if (has('needs_review')) return 'needs_review';
  if (list.every((u) => u.status === 'not_run')) return 'not_run';
  if (has('not_run')) return 'needs_review';
  if (has('waived')) return 'waived';
  return 'met';
}

export function unsignableReason(criterionId, units) {
  const BLOCK = { not_met: 'not met', not_covered: 'not covered', needs_review: 'needs review', not_run: 'not run' };
  const blockers = (Array.isArray(units) ? units : []).filter((u) => BLOCK[u.status])
    .map((u) => ({ unitId: u.id, status: u.status, ...(u.reason ? { reason: u.reason } : {}) }));
  if (!blockers.length) return null;
  const text = `${criterionId}: ${blockers.map((b) => `unit ${b.unitId} ${BLOCK[b.status]}${b.reason ? ` (${b.reason})` : ''}`).join('; ')}`;
  return { blockers, text };
}

export function noBranchApplies(flag, matchers) {
  if (!flag || flag.present !== true) return false;
  return !(Array.isArray(matchers) ? matchers : []).some((m) => { try { return m(flag.value); } catch { return false; } });
}

export function unitsOf(criterion) {
  const c = criterion && typeof criterion === 'object' ? criterion : {};
  const ids = (a) => (Array.isArray(a) ? a.filter(Boolean) : []);
  let out = [];
  const variants = c.scope && Array.isArray(c.scope.variants) ? c.scope.variants.filter((v) => v && v.id) : [];
  if (Array.isArray(c.branches) && c.branches.length) {
    out = c.branches.filter((b) => b && b.id).map((b) => ({ id: String(b.id), checkIds: ids(b.checkIds), kind: 'branch' }));
  } else if (variants.length) {
    out = variants.map((v) => ({ id: String(v.id), checkIds: ids(v.checkIds), kind: 'variant' }));
  } else {
    const checkIds = ids(c.checkIds);
    if (checkIds.length) out = [{ id: DEFAULT_UNIT, checkIds, kind: 'default' }];
  }
  if (Array.isArray(c.observe) && c.observe.length) out.push({ id: OBSERVE_UNIT, checkIds: [], kind: 'observe' });
  return out;
}

// T21 twin of launch/units.js observeUnitStatus / variantUnitStatus / noBranchUnit. The frontend never
// reads rows: `observed` is the proxy's observeCriterion() output (or absent -> not_run).
export function observeUnitStatus(observed, opts = {}) {
  try {
    if (!observed || typeof observed !== 'object' || !Array.isArray(observed.items)) return { status: 'not_run' };
    const items = observed.items.map((i) => ({ label: i.label, kind: i.kind, read: i.read === true }));
    const unread = items.filter((i) => !i.read).map((i) => i.label);
    if (!items.length) return { status: 'not_run' };
    if (unread.length) {
      return { status: opts.waived ? 'waived' : 'not_covered', reason: `unread: ${unread.join(', ')}`, items, unread };
    }
    return { status: 'met', items, unread, graded: Number(observed.rowsInScope) || 0 };
  } catch {
    return { status: 'not_run' };
  }
}

export function variantUnitStatus(info, opts = {}) {
  try {
    if (!info || typeof info !== 'object') return { status: 'not_run' };
    const graded = Number(info.graded) || 0;
    const unreadable = Number(info.unreadable) || 0;
    if (graded > 0) return { status: 'met', graded, unreadable };
    return { status: opts.waived ? 'waived' : 'not_covered', reason: 'no_rows_matched', unreadable };
  } catch {
    return { status: 'not_run' };
  }
}

export function noBranchUnit(observed, opts = {}) {
  const n = observed && observed.noBranchApplies ? Number(observed.noBranchApplies.count) || 0 : 0;
  if (!(n > 0)) return null;
  return { id: NO_BRANCH_UNIT, checkIds: [], kind: 'branch', rows: n, ...unitStatus([], { reason: UNIT_REASONS.no_branch_applies, ...opts }) };
}

export function criterionRollup(criterion, resultsByCheckId, opts = {}) {
  try {
    const c = criterion && typeof criterion === 'object' ? criterion : {};
    const id = c.id || '';
    const attached = Array.isArray(c.baselineIds) ? c.baselineIds : [];
    const waived = new Set(Array.isArray(opts.waivedUnitIds) ? opts.waivedUnitIds : []);
    if (c.mapping === 'manual') return { status: 'manual', legacyStatus: CRITERION_STATUS.MANUAL_REVIEW, units: [], unsignableReason: null };
    const observed = opts.observed && typeof opts.observed === 'object' ? opts.observed : null;
    const list = c.mapping === 'unmapped' ? [] : unitsOf(c).map((u) => {
      const w = waived.has(u.id);
      let st;
      if (u.kind === 'observe') st = observeUnitStatus(observed, { waived: w });
      else if (u.kind === 'variant' && !u.checkIds.length) st = variantUnitStatus(observed && observed.variants && observed.variants[u.id], { waived: w });
      else {
        st = unitStatus(
          u.checkIds.map((checkId) => ({ checkId, result: lookupResult(resultsByCheckId, checkId) })),
          { attached, waived: w },
        );
      }
      return { id: u.id, checkIds: u.checkIds, kind: u.kind, ...st };
    });
    const nb = c.mapping === 'unmapped' ? null : noBranchUnit(observed, { waived: waived.has(NO_BRANCH_UNIT) });
    if (nb) list.push(nb);
    const status = unitsCriterionStatus(list);
    return { status, legacyStatus: toLegacyStatus(status), units: list, unsignableReason: unsignableReason(id, list) };
  } catch {
    return { status: 'not_run', legacyStatus: CRITERION_STATUS.NOT_RUN, units: [], unsignableReason: null };
  }
}

// ---------------------------------------------------------------------------
// LAUNCH_REDESIGN answer fields (launch-redesign T14: C18, C19, C29, C34). Twin of
// rollupAnswerFields / notCoveredReason in proxy-server/launch/buildRollup.js (parity:
// launchRollupParity.test.js). The frontend reads `mode` from the proxy answer; this is the
// fallback when the proxy does not answer. opts: { mode, waivedUnitIds: {criterionId: unitId[]},
// legacyById: {criterionId: hyphen status} }. Returns null in `off`. Never throws.
// ---------------------------------------------------------------------------
const REDESIGN_MODES = ['off', 'shadow', 'on'];
const isPlain = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

export function rollupAnswerFields(criteria, resultsByCheckId, opts) {
  try {
    const o = isPlain(opts) ? opts : {};
    const mode = REDESIGN_MODES.includes(o.mode) ? o.mode : 'shadow';
    if (mode === 'off') return null;
    const waivedMap = isPlain(o.waivedUnitIds) ? o.waivedUnitIds : {};
    const legacyById = isPlain(o.legacyById) ? o.legacyById : {};
    const observedMap = isPlain(o.observedById) ? o.observedById : {};
    const list = Array.isArray(criteria) ? criteria : [];
    const uncoveredUnitList = [];
    const unconfirmedCriteria = [];
    let waivedCount = 0;
    const perCriterion = list.map((c) => {
      const cid = c && c.id;
      const waived = Array.isArray(waivedMap[cid]) ? waivedMap[cid] : [];
      const r = criterionRollup(c, resultsByCheckId, { waivedUnitIds: waived, observed: observedMap[cid] });
      const legacyStatus = legacyById[cid] != null ? legacyById[cid] : criterionStatus(c, resultsByCheckId, mode);
      const unconfirmed = mode === 'shadow' && legacyStatus === 'met' && r.status === 'not_covered';
      const units = r.units.map((u) => {
        const out = { ...u };
        if (unconfirmed && u.status === 'not_covered') out.unconfirmed = true;
        return out;
      });
      for (const u of units) {
        if (u.status === 'not_covered') uncoveredUnitList.push({ criterionId: cid, unitId: u.id });
        if (u.status === 'waived') waivedCount += 1;
      }
      if (unconfirmed) unconfirmedCriteria.push(cid);
      return { criterionId: cid, status: r.status, legacyStatus, unconfirmed, units, unsignableReason: r.unsignableReason };
    });
    return {
      mode,
      uncoveredUnits: uncoveredUnitList.length,
      uncoveredUnitList,
      waivedCount,
      header: waivedCount > 0 ? `${waivedCount} branches not verified (waived)` : null,
      unconfirmedCriteria,
      perCriterion,
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// T34: `signable` for LAUNCH_REDESIGN=on (T33 ruling, 28-plan.md section 0). Twin of the `unitModelOn`
// branch in proxy-server/launch/buildRollup.js (parity: launchRollupParity.test.js runs the real
// buildRollup and compares). `entries` are the buildRollup `criteria[]` shape ({ criterionId, downgraded?,
// redesign: { status } }). In `on`: at least one criterion, every entry's unit status in {met, waived}, no
// low-confidence downgrade, no material disagreement, no mixed owners; the legacy verdict/coverage is NOT
// ANDed. `off`/`shadow` return the caller's legacy signable unchanged and `blockers: null`. Never throws.
// ---------------------------------------------------------------------------
export function rollupSignable(args) {
  try {
    const { mode, entries, legacySignable, hasMaterialDisagreement, mixedBlocks, redesign } = args || {};
    // Twin of buildRollup `unitModelOn = !!redesign && mode === 'on'`: a rollup whose top-level `redesign`
    // is null (pass `redesign: out.redesign`; undefined = not supplied) falls back to the legacy rule.
    if (mode !== 'on' || redesign === null) return { signable: legacySignable === true, blockers: null };
    const list = Array.isArray(entries) ? entries : [];
    const ok = (e) => !!e && !!e.redesign && (e.redesign.status === 'met' || e.redesign.status === 'waived')
      && e.downgraded !== 'low_confidence';
    const blockers = list.filter((e) => !ok(e));
    return { signable: list.length > 0 && blockers.length === 0 && !hasMaterialDisagreement && !mixedBlocks, blockers };
  } catch {
    return { signable: false, blockers: [] };
  }
}

// ---------------------------------------------------------------------------
// C21: which answer the Launch page shows. The proxy's answer whenever it responded (a boolean `signable`
// is the shape check); the local fallback only when it did not, and then the page says so. Pure.
// ---------------------------------------------------------------------------
export function selectRollup(proxyAnswer, fallbackAnswer) {
  const valid = (a) => a != null && typeof a === 'object' && typeof a.signable === 'boolean';
  if (valid(proxyAnswer)) return { origin: 'proxy', answer: proxyAnswer, notice: null };
  if (valid(fallbackAnswer)) return { origin: 'fallback', answer: fallbackAnswer, notice: UI_WORDING.fallbackNotice };
  return { origin: 'none', answer: null, notice: UI_WORDING.noAnswerNotice };
}

// The coverage-gap code (naReason) behind a Not covered flat unit: the first of its checks whose result is
// n/a for a gap reason. The rollup answer's unit carries no naReason for flat units, so the page reads it
// from the local run results it already holds. null when none.
export function unitGapReason(unit, resultsByCheckId) {
  try {
    for (const id of (unit && Array.isArray(unit.checkIds) ? unit.checkIds : [])) {
      const r = lookupResult(resultsByCheckId, id);
      const reason = effectiveNaReason(r);
      if (reason && COVERAGE_GAP_REASONS.includes(reason)) return reason;
    }
  } catch { /* fall through */ }
  return null;
}

export function notCoveredReason(uncoveredUnitList) {
  const by = new Map();
  for (const u of uncoveredUnitList) {
    if (!by.has(u.criterionId)) by.set(u.criterionId, []);
    by.get(u.criterionId).push(u.unitId);
  }
  return `not_covered: ${[...by].map(([cid, ids]) => `${cid}: ${ids.map((i) => `unit ${i} not covered`).join('; ')}`).join(' | ')}`;
}
